<template>
  <div class="process-category-manager">
    <!-- Selector de proceso -->
    <div class="process-selector">
      <el-row :gutter="16">
        <el-col :span="18">
          <CustomSelect
            v-model="procesoSeleccionado"
            :span-column="24"
            :field-key="'Codigo'"
            :field-label="'Descripcion'"
            :label="'Seleccionar proceso'"
            :settings="{
              filterable: true,
              clearable: true,
              options: procesosDisponibles,
              placeholder: 'Selecciona un proceso',
            }"
            :store-object="true"
          />
        </el-col>
        <el-col
          :span="6"
          class="button-col"
        >
          <el-button
            type="primary"
            :disabled="
              !procesoSeleccionado ||
              !procesoSeleccionado.Codigo ||
              !procesosDisponibles ||
              procesosDisponibles.length === 0
            "
            @click="agregarProceso"
          >
            + Agregar proceso
          </el-button>
        </el-col>
      </el-row>
    </div>

    <!-- Lista de procesos con categorías -->
    <el-card
      v-for="(item, index) in procesosConCategorias"
      :key="`cat-${item._uid}`"
      class="process-card collapsible-card"
      shadow="hover"
      :body-style="{ padding: '6px' }"
    >
      <!-- Header de la card siempre visible -->
      <el-row
        :gutter="8"
        class="process-header-row"
      >
        <el-col
          :xs="18"
          :sm="16"
        >
          <div
            class="process-name-collapsible"
            @click="toggleProcesoCollapse(item._uid)"
          >
            <el-icon
              class="collapse-icon"
              :class="{ expanded: !isProcesoColapsado(item._uid) }"
            >
              <ArrowDown />
            </el-icon>
            <span>{{ item.Proceso.Descripcion || '—' }}</span>
          </div>
        </el-col>
        <el-col
          :xs="6"
          :sm="8"
          class="button-col"
        >
          <el-button
            size="small"
            plain
            :icon="IconAdd"
            @click="addCategoriaForProceso(item)"
          >
            Agregar categoría
          </el-button>
          <el-button
            size="small"
            type="danger"
            plain
            :icon="IconDelete"
            title="Eliminar proceso"
            @click="removeProceso(index)"
          >
            Eliminar
          </el-button>
        </el-col>
      </el-row>

      <!-- Contenido colapsable - lista de categorías -->
      <transition name="collapse">
        <div
          v-show="!isProcesoColapsado(item._uid)"
          class="categories-content"
        >
          <el-row
            v-for="(categoria, catIndex) in item.categorias"
            :key="categoria._uid"
            :gutter="8"
            class="category-row"
          >
            <el-col
              :xs="24"
              :sm="6"
            >
              <CustomSelect
                v-model="categoria.CategoriaMaterial"
                :span-column="24"
                :field-key="'Codigo'"
                :field-label="'Descripcion'"
                :label="'Categoría de item'"
                :settings="{
                  filterable: true,
                  clearable: true,
                  options: getCategoriasDisponiblesParaCategoria(
                    item,
                    categoria.CategoriaMaterial?.Codigo,
                  ),
                  placeholder: 'Seleccionar',
                }"
                :store-object="true"
                @update:model-value="onCategoriaSelectChanged(categoria, $event)"
              />
            </el-col>
            <el-col
              :xs="8"
              :sm="3"
            >
              <el-form-item label="MultiMaterial">
                <el-switch
                  v-model="categoria.MultiMaterial"
                  @change="onMultiMaterialChanged(item, categoria)"
                />
              </el-form-item>
            </el-col>
            <el-col
              :xs="8"
              :sm="3"
            >
              <el-form-item label="Suma Gramaje">
                <el-switch
                  v-model="categoria.SumaGramaje"
                  @change="onSumaGramajeChanged(item, categoria)"
                />
              </el-form-item>
            </el-col>
            <el-col
              :xs="8"
              :sm="4"
            >
              <el-form-item
                label="Dim. Automáticas"
                title="Traslada ancho y largo del suaje automáticamente al cotizar"
              >
                <el-switch v-model="categoria.DimensionesAutomaticas" />
              </el-form-item>
            </el-col>
            <el-col
              :xs="8"
              :sm="4"
            >
              <el-form-item
                label="Cantidad Manual"
                title="COTI-635: en ET/OT permite cargar materiales reales (cantidad manual) además del teórico calculado"
              >
                <el-switch
                  v-model="categoria.CantidadManual"
                  @change="onCantidadManualChanged(item, categoria)"
                />
              </el-form-item>
            </el-col>
            <el-col
              :xs="8"
              :sm="4"
            >
              <el-form-item
                label="Permite Reservar"
                title="COTI-635: habilita reservar la cantidad manual cargada en ET/OT para esta categoría (requiere Cantidad Manual activo)"
              >
                <el-switch
                  v-model="categoria.PermiteReservar"
                  @change="onPermiteReservarChanged(item, categoria)"
                />
              </el-form-item>
            </el-col>
            <el-col
              :xs="8"
              :sm="5"
            >
              <el-form-item
                label="Proceso precedente"
                title="Muestra en la grilla de materiales una columna para indicar de qué proceso proviene cada material (esos materiales no se costean)"
              >
                <el-switch v-model="categoria.ProcesoPrecedente" />
              </el-form-item>
            </el-col>
            <el-col
              :xs="24"
              :sm="3"
              class="action-col"
            >
              <el-button
                circle
                size="small"
                type="danger"
                :icon="IconDelete"
                aria-label="Eliminar categoría"
                title="Eliminar categoría"
                @click="removeCategoria(item, catIndex)"
              />
            </el-col>
          </el-row>

          <!-- Mensaje cuando no hay categorías -->
          <div
            v-if="item.categorias.length === 0"
            class="no-categories"
          >
            <el-text
              type="info"
              size="small"
              >No hay categorías agregadas para este proceso</el-text
            >
          </div>
        </div>
      </transition>
    </el-card>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  nextTick,
  shallowRef,
} from 'vue';
import {
  ElMessage,
  ElMessageBox,
} from 'element-plus';
import {
  Plus as IconAdd,
  Delete as IconDelete,
  ArrowDown,
} from '@element-plus/icons-vue';
import CustomSelect from '@/shared/components/custom_select.vue';

// Props
const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  procesosDisponibles: {
    type: Array,
    default: () => [],
  },
  // todosLosProcesos: {
  //   type: Array,
  //   default: () => []
  // },
  categoriasDisponibles: {
    type: Array,
    default: () => [],
  },
});

// Emits
const emit = defineEmits([
  'update:modelValue',
  'categoria-changed',
  'multi-material-changed',
  'cantidad-manual-changed',
  'permite-reservar-changed',
  'categoria-added',
]);

// Estado local - Optimizado para rendimiento
const procesoSeleccionado = ref(null);
const procesosColapsados = shallowRef(new Set()); // Usar shallowRef para mejor rendimiento

// Computed
const procesosConCategorias = computed({
  get: () => props.modelValue || [],
  set: (value) => emit('update:modelValue', value),
});

// Función para generar IDs únicos
const uid = () => Math.random().toString(36).slice(2, 9);

// Métodos utilitarios
// const getProcesoLabel = (codigoProceso) => {
//   // Primero buscar en todos los procesos cargados usando el Codigo
//   const procesoCompleto = props.todosLosProcesos.find(p => p.Codigo === codigoProceso)
//   if (procesoCompleto) {
//     return procesoCompleto.Descripcion
//   }

//   // Fallback: buscar en procesos disponibles usando Codigo
//   const proceso = props.procesosDisponibles.find(p => p.Codigo === codigoProceso)
//   return proceso?.Descripcion || `Proceso ${codigoProceso}`
// }

// Cache para categorías disponibles - optimización de rendimiento
const categoriesCache = new Map();

const getCategoriasDisponiblesParaCategoria = (proceso, codigoCategoria) => {
  if (!proceso || !proceso.categorias) return props.categoriasDisponibles;

  // Crear clave única para el cache
  const cacheKey = `${proceso._uid}-${codigoCategoria || 'null'}`;

  // Verificar cache
  if (categoriesCache.has(cacheKey)) {
    return categoriesCache.get(cacheKey);
  }

  const categoriasUsadas = proceso.categorias
    .map((c) => c.CategoriaMaterial?.Codigo)
    .filter((codigo) => codigo && codigo !== codigoCategoria);

  const resultado = props.categoriasDisponibles.filter(
    (cat) => !categoriasUsadas.includes(cat.Codigo),
  );

  // Guardar en cache
  categoriesCache.set(cacheKey, resultado);

  return resultado;
};

// Limpiar cache cuando cambien las props relevantes
watch(
  () => [props.categoriasDisponibles, procesosConCategorias.value],
  () => {
    categoriesCache.clear();
  },
  { deep: true },
);

// Watcher para detectar cambios en las categorías de material y emitir evento
watch(
  () => procesosConCategorias.value,
  (newValue, oldValue) => {
    if (!newValue) return;

    // Detectar cambios en las categorías de material
    newValue.forEach((proceso, procesoIndex) => {
      if (!proceso.categorias) return;

      proceso.categorias.forEach((categoria, categoriaIndex) => {
        // Obtener el estado anterior si existe
        const oldProceso = oldValue?.[procesoIndex];
        const oldCategoria = oldProceso?.categorias?.[categoriaIndex];

        // Verificar si cambió la categoría de material
        const nuevoCodigo = categoria.CategoriaMaterial?.Codigo;
        const viejoCodigo = oldCategoria?.CategoriaMaterial?.Codigo;

        // Emitir evento en los siguientes casos:
        // 1. Cambio de una categoría existente a otra
        // 2. Selección inicial de una categoría (de null a un valor)
        const huboSeleccionInicial = !viejoCodigo && nuevoCodigo;
        const huboCambioCategoria = oldCategoria && nuevoCodigo !== viejoCodigo && nuevoCodigo;

        if (huboSeleccionInicial || huboCambioCategoria) {
          // Emitir evento inmediatamente
          emit('categoria-changed', categoria);
        }
      });
    });
  },
  { deep: true, flush: 'post' },
);

const getCategoriaSeleccionada = (categoria) => {
  if (!categoria.CategoriaMaterial?.Codigo) return null;

  // Buscar el objeto completo de la categoría basado en el código almacenado
  return (
    props.categoriasDisponibles.find((cat) => cat.Codigo === categoria.CategoriaMaterial.Codigo) ||
    null
  );
};

// Métodos de gestión de procesos
const agregarProceso = () => {
  if (!procesoSeleccionado.value) {
    ElMessage.warning('Selecciona un proceso primero');
    return;
  }

  if (!procesoSeleccionado.value.Codigo) {
    console.error('Proceso seleccionado no tiene Codigo válido:', procesoSeleccionado.value);
    ElMessage.error('Error: Proceso seleccionado no es válido');
    return;
  }

  const nuevoProceso = {
    _uid: uid(),
    CodigoProceso: procesoSeleccionado.value.Codigo,
    Proceso: procesoSeleccionado.value,
    categorias: [],
  };

  // Agregar a procesos colapsados por defecto
  procesosColapsados.value.add(nuevoProceso._uid);

  const nuevaLista = [...procesosConCategorias.value, nuevoProceso];
  procesosConCategorias.value = nuevaLista;
  procesoSeleccionado.value = null;
  ElMessage.success('Proceso agregado correctamente');
};

const addCategoriaForProceso = (proceso) => {
  if (!proceso.Proceso) {
    ElMessage.warning('Error: Proceso no válido.');
    return;
  }

  // Expandir la card automáticamente al agregar categoría
  procesosColapsados.value.delete(proceso._uid);

  proceso.categorias.push({
    _uid: uid(),
    Proceso: proceso.Proceso,
    CategoriaMaterial: null,
    MultiMaterial: false,
    SumaGramaje: false,
    DimensionesAutomaticas: false,
    CantidadManual: false,
    PermiteReservar: false,
    ProcesoPrecedente: false,
  });

  // Emitir cambio
  procesosConCategorias.value = [...procesosConCategorias.value];
};

const removeProceso = async (procesoIndex) => {
  const proceso = procesosConCategorias.value[procesoIndex];
  if (!proceso) return;

  try {
    await ElMessageBox.confirm(
      `¿Estás seguro de que deseas eliminar el proceso "${proceso.Proceso.Descripcion}"?${proceso.categorias.length > 0 ? ` Esto también eliminará las ${proceso.categorias.length} categoría(s) asociada(s).` : ''}`,
      'Confirmar eliminación',
      {
        confirmButtonText: 'Eliminar',
        cancelButtonText: 'Cancelar',
        type: 'warning',
        confirmButtonClass: 'el-button--danger',
      },
    );

    // Si el usuario confirma, proceder con la eliminación
    // Remover del estado de colapso
    procesosColapsados.value.delete(proceso._uid);

    // Remover de la lista
    const nuevaLista = procesosConCategorias.value.filter((_, index) => index !== procesoIndex);
    procesosConCategorias.value = nuevaLista;

    ElMessage.success('Proceso eliminado correctamente');
  } catch (error) {
    // Usuario canceló la operación
    ElMessage.info('Eliminación cancelada');
  }
};

const removeCategoria = async (proceso, catIndex) => {
  proceso.categorias.splice(catIndex, 1);

  // Emitir cambio
  procesosConCategorias.value = [...procesosConCategorias.value];
};

const onCategoriaChanged = (categoria) => {
  // Emitir evento específico para que el componente padre maneje la lógica relacionada
  emit('categoria-changed', categoria);

  // Solo forzar reactividad al final si realmente es necesario
  nextTick(() => {
    procesosConCategorias.value = [...procesosConCategorias.value];
  });
};

const onCategoriaSelectChanged = (categoria, categoriaSeleccionada) => {
  // Actualizar la categoría de material
  const categoriaAnterior = categoria.CategoriaMaterial?.Codigo;
  categoria.CategoriaMaterial = categoriaSeleccionada;

  // Emitir evento si hay un cambio válido
  if (categoriaSeleccionada?.Codigo) {
    emit('categoria-changed', categoria);

    // También llamar a la función interna para compatibilidad
    onCategoriaChanged(categoria);
  } else if (categoriaAnterior) {
    // Si se limpia la categoría (se selecciona null), también notificar
    emit('categoria-changed', categoria);
  }

  // Forzar reactividad
  nextTick(() => {
    procesosConCategorias.value = [...procesosConCategorias.value];
  });
};

const onMultiMaterialChanged = (proceso, categoria) => {
  // Emitir cambio en el modelo primero
  procesosConCategorias.value = [...procesosConCategorias.value];

  // Emitir evento específico para que el componente padre maneje la lógica relacionada
  emit('multi-material-changed', { proceso, categoria });
};

const onSumaGramajeChanged = (proceso, categoria) => {
  // Emitir cambio en el modelo primero
  procesosConCategorias.value = [...procesosConCategorias.value];

  // Emitir evento específico para que el componente padre maneje la lógica relacionada
  emit('suma-gramaje-changed', { proceso, categoria });
};

// COTI-635: igual patrón que onMultiMaterialChanged/onSumaGramajeChanged.
// Interlock inverso: si se desactiva Cantidad Manual mientras Permite Reservar está
// activo, se desactiva también Permite Reservar — no puede existir una reserva sin
// una cantidad manual que reservar.
const onCantidadManualChanged = (proceso, categoria) => {
  if (!categoria.CantidadManual && categoria.PermiteReservar) {
    categoria.PermiteReservar = false;
  }
  procesosConCategorias.value = [...procesosConCategorias.value];
  emit('cantidad-manual-changed', { proceso, categoria });
};

// COTI-635: activar Permite Reservar activa automáticamente Cantidad Manual (no puede
// haber reserva sin cantidad manual). Desactivar Permite Reservar NO desactiva
// Cantidad Manual, que sigue siendo manipulable de forma independiente.
const onPermiteReservarChanged = (proceso, categoria) => {
  if (categoria.PermiteReservar) {
    categoria.CantidadManual = true;
  }
  procesosConCategorias.value = [...procesosConCategorias.value];
  emit('permite-reservar-changed', { proceso, categoria });
};

// Métodos de collapse - Optimizados
const toggleProcesoCollapse = (procesoUid) => {
  const newSet = new Set(procesosColapsados.value);
  if (newSet.has(procesoUid)) {
    newSet.delete(procesoUid);
  } else {
    newSet.add(procesoUid);
  }
  procesosColapsados.value = newSet;
};

const isProcesoColapsado = (procesoUid) => {
  return procesosColapsados.value.has(procesoUid);
};

// Exponer métodos necesarios
defineExpose({
  inicializarEstadoColapso: () => {
    // Inicializar collapse para procesos existentes
    procesosConCategorias.value.forEach((proceso) => {
      procesosColapsados.value.add(proceso._uid);
    });
  },
});
</script>

<style scoped>
.process-category-manager {
  width: 100%;
}

.process-selector {
  margin-bottom: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.process-card {
  margin-bottom: 6px;
}

.process-card :deep(.el-card__body) {
  padding: 6px;
}

.category-row {
  margin-bottom: 4px;
  align-items: flex-end;
}

.button-col {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  padding-bottom: 8px;
  gap: 8px;
}

.action-col {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  padding-bottom: 8px;
}

.collapsible-card {
  overflow: hidden;
}

.process-header-row {
  margin-bottom: 0;
  align-items: center;
}

.process-name-collapsible {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  color: var(--el-text-color-primary);
  padding: 8px 0;
  min-height: 32px;
  user-select: none;
}

.process-name-collapsible:hover {
  color: var(--el-color-primary);
}

.collapse-icon {
  transition: transform 0.3s ease;
  transform: rotate(-90deg);
  color: var(--el-text-color-secondary);
}

.collapse-icon.expanded {
  transform: rotate(0deg);
}

/* Optimizaciones de rendimiento CSS */
.process-card {
  contain: layout style paint;
  transform: translateZ(0); /* Crear capa de composición */
}

.categories-content {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--el-border-color-lighter);
  contain: layout style; /* Contener cambios de layout */
}

.no-categories {
  padding: 16px 0;
  text-align: center;
  color: var(--el-text-color-placeholder);
  font-style: italic;
}

/* Transición para el collapse - Optimizada para mayor velocidad */
.collapse-enter-active,
.collapse-leave-active {
  transition: all 0.15s ease-out;
  overflow: hidden;
  will-change: max-height, opacity;
}

.collapse-enter-from,
.collapse-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-5px);
}

.collapse-enter-to,
.collapse-leave-from {
  max-height: 800px; /* Reducido de 1000px */
  opacity: 1;
  transform: translateY(0);
}

.process-card .category-row .action-col {
  min-height: 48px;
}

.process-card .category-row .action-col .el-button {
  margin-top: 2px;
}

.process-card .button-col {
  min-height: 32px;
  padding-bottom: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.process-card .button-col .el-button {
  margin-top: 0;
}

.process-card :deep(.el-form-item) {
  margin-bottom: 8px;
}

.process-card :deep(.el-form-item__label) {
  padding-bottom: 2px;
  font-size: 12px;
  line-height: 1.2;
}
</style>
